// Normalização de coordenadas de carona.
//
// No Mongo as caronas são gravadas em GeoJSON: `origin.location.coordinates`
// = [lng, lat] (ver POST /api/rides). O front, porém, consome `lat`/`lng` no
// nível do objeto. Qualquer caminho que leia a carona direto do banco precisa
// passar por aqui — ler o documento cru entrega `undefined` e quebra o mapa
// (bug do RideRouteMap: "Invalid LatLng object: (undefined, undefined)").
//
// Convenção: `typeof x === "number"`, nunca `||` — lat/lng 0 são válidos
// (checklist #3 do AGENTS.md: `0 || fallback` descarta 0 silenciosamente).

// Sem index signature de propósito: com `[key: string]: unknown`, o TypeScript
// deixa de aceitar `StopLike` (de `lib/route-denormalize.ts`) aqui, porque
// interface sem index signature não é atribuível a um tipo que tem uma. Como
// esta forma é estrutural, dispensar a assinatura deixa o helper reutilizável
// pelas interfaces que já existem no projeto.
export interface RideLocationLike {
  name?: string
  placeId?: string
  price?: number
  order?: number
  alongKm?: number
  lat?: number
  lng?: number
  location?: { type?: string; coordinates?: number[] }
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

/**
 * Extrai `{ lat, lng }` de um objeto que pode estar achatado (`{lat, lng}`) ou
 * em GeoJSON (`{location: {coordinates: [lng, lat]}}`). Devolve `null` quando
 * nenhum dos dois formatos tem coordenada utilizável.
 *
 * A ordem GeoJSON-primeiro é intencional: um documento gravado pelo POST tem
 * apenas `location`; um documento antigo/editado pelo manage-ride pode ter
 * apenas `lat`/`lng`. Se os dois existirem e divergirem, o GeoJSON é a fonte
 * de verdade porque foi o denormalizador que gravou.
 */
export function locationPoint(
  loc: RideLocationLike | null | undefined
): { lat: number; lng: number } | null {
  if (!loc) return null

  const coords = loc.location?.coordinates
  if (Array.isArray(coords) && coords.length >= 2 && finite(coords[1]) && finite(coords[0])) {
    return { lat: coords[1], lng: coords[0] }
  }
  if (finite(loc.lat) && finite(loc.lng)) {
    return { lat: loc.lat, lng: loc.lng }
  }
  return null
}

/**
 * Devolve uma cópia do local com `lat`/`lng` no nível do objeto.
 * Preserva todos os outros campos (name, placeId, price, order, alongKm) e o
 * `location` original — o mapa e o denormalizador continuam funcionando, e
 * quem precisar do GeoJSON original ainda o tem.
 *
 * Se não houver coordenada alguma, o local sai intacto (sem `lat`/`lng`
 * inventados) para que o consumidor possa detectar e tratar a ausência.
 */
export function normalizeLatLng<T extends RideLocationLike>(loc: T): T {
  if (!loc || typeof loc !== "object") return loc
  const point = locationPoint(loc)
  if (!point) return loc
  return { ...loc, lat: point.lat, lng: point.lng }
}

/** A forma da carona que o helper sabe normalizar. */
interface RideLike {
  origin?: RideLocationLike | null
  destination?: RideLocationLike | null
  stops?: Array<RideLocationLike | null> | null
}

/**
 * Normaliza `origin`, `destination` e `stops` de uma carona.
 * Aceita `null`/`undefined` e devolve `null` no lugar, para o chamador poder
 * distinguir "carona sem origem" de "carona não encontrada".
 *
 * Sem constrain em `T`: o documento do driver do Mongo (`WithId<Document>`) não
 * é atribuível a uma forma com `origin?`/`stops?` opcionais, e obrigar o
 * chamador a castear aqui só empurraria o `any` para fora.
 */
export function normalizeRideLocations<T>(ride: T | null | undefined): T | null {
  if (!ride || typeof ride !== "object") return null

  const source = ride as unknown as RideLike
  // `{ ...source }` e não `Object.assign`: os campos que não são
  // origin/destination/stops precisam sobreviver intactos (id, data, preço...).
  const result = { ...source } as Record<string, unknown>

  if (source.origin) {
    result.origin = normalizeLatLng(source.origin)
  }
  if (source.destination) {
    result.destination = normalizeLatLng(source.destination)
  }
  if (Array.isArray(source.stops)) {
    result.stops = source.stops.map((stop) => (stop ? normalizeLatLng(stop) : stop))
  }

  return result as T
}
